import { useUser, UserProfile } from '@clerk/react'
import { dark } from '@clerk/themes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthenticatedFetch } from '../lib/api'

interface ReplicateKeyState {
  has_key: boolean
  preview: string | null
}

interface BillingProduct {
  id: string
  name: string
  description: string
  credits: number
  price_usd: number
  price_zar: number
  highlighted: boolean
  available: boolean
}

interface PurchaseRecord {
  id: string
  catalog_id: string | null
  amount_cents: number | null
  currency: string | null
  credits_granted: number
  status: string
  created_at: string
}

export default function Settings() {
  const { user, isLoaded } = useUser()
  const authenticatedFetch = useAuthenticatedFetch()

  const [keyState, setKeyState] = useState<ReplicateKeyState | null>(null)
  const [keyInput, setKeyInput] = useState('')
  const [keyBusy, setKeyBusy] = useState(false)
  const [keyMsg, setKeyMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null,
  )

  // Billing state
  const [products, setProducts] = useState<BillingProduct[]>([])
  const [purchases, setPurchases] = useState<PurchaseRecord[]>([])
  const [buyingId, setBuyingId] = useState<string | null>(null)
  const [billingMsg, setBillingMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null,
  )
  const billingSuccess = useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('billing') === 'success'
  }, [])

  const refreshKey = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/user/replicate-key')
      const { data } = (await res.json()) as { data: ReplicateKeyState }
      setKeyState(data)
    } catch (err) {
      // Non-fatal — just show as "no key"
      console.warn('Failed to load replicate key state:', err)
    }
  }, [authenticatedFetch])

  useEffect(() => {
    refreshKey()
  }, [refreshKey])

  const handleSaveKey = useCallback(async () => {
    setKeyBusy(true)
    setKeyMsg(null)
    try {
      const res = await authenticatedFetch('/api/user/replicate-key', {
        method: 'POST',
        body: JSON.stringify({ api_key: keyInput.trim() }),
      })
      const { data } = (await res.json()) as { data: ReplicateKeyState }
      setKeyState(data)
      setKeyInput('')
      setKeyMsg({ kind: 'ok', text: 'Saved. Replicate models are now available.' })
    } catch (err) {
      setKeyMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to save key',
      })
    } finally {
      setKeyBusy(false)
    }
  }, [authenticatedFetch, keyInput])

  const handleDeleteKey = useCallback(async () => {
    setKeyBusy(true)
    setKeyMsg(null)
    try {
      await authenticatedFetch('/api/user/replicate-key', { method: 'DELETE' })
      setKeyState({ has_key: false, preview: null })
      setKeyMsg({ kind: 'ok', text: 'Key removed.' })
    } catch (err) {
      setKeyMsg({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to remove key',
      })
    } finally {
      setKeyBusy(false)
    }
  }, [authenticatedFetch])

  // ── Billing ────────────────────────────────────────────────────────
  const refreshProducts = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/billing/products')
      const { data } = (await res.json()) as { data: BillingProduct[] }
      setProducts(data)
    } catch (err) {
      console.warn('Failed to load billing products:', err)
    }
  }, [authenticatedFetch])

  const refreshPurchases = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/billing/purchases')
      const { data } = (await res.json()) as { data: PurchaseRecord[] }
      setPurchases(data)
    } catch (err) {
      console.warn('Failed to load purchases:', err)
    }
  }, [authenticatedFetch])

  useEffect(() => {
    refreshProducts()
    refreshPurchases()
  }, [refreshProducts, refreshPurchases])

  const handleBuy = useCallback(
    async (catalogId: string) => {
      setBuyingId(catalogId)
      setBillingMsg(null)
      try {
        const res = await authenticatedFetch('/api/billing/checkout', {
          method: 'POST',
          body: JSON.stringify({ catalog_id: catalogId }),
        })
        const { data } = (await res.json()) as { data: { url: string } }
        if (data?.url) {
          window.location.href = data.url
          return
        }
        throw new Error('Checkout URL missing')
      } catch (err) {
        setBillingMsg({
          kind: 'err',
          text: err instanceof Error ? err.message : 'Checkout failed',
        })
        setBuyingId(null)
      }
    },
    [authenticatedFetch],
  )

  // Clean ?billing=success out of the URL after we've read it so a
  // refresh doesn't re-trigger the banner.
  useEffect(() => {
    if (billingSuccess && typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.delete('billing')
      window.history.replaceState({}, '', url.toString())
    }
  }, [billingSuccess])

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-neon border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto p-6 space-y-8">
        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>

        <div className="bg-bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Account</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wider text-text-muted">
                Email
              </span>
              <p className="text-sm text-text-primary">
                {user?.primaryEmailAddress?.emailAddress ?? 'No email'}
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wider text-text-muted">
                Name
              </span>
              <p className="text-sm text-text-primary">
                {user?.fullName ?? 'Not set'}
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wider text-text-muted">
                Role
              </span>
              <p className="text-sm text-text-primary">
                {(user?.publicMetadata?.role as string) ?? 'Member'}
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wider text-text-muted">
                Credits remaining
              </span>
              <p className="text-sm text-neon font-semibold">
                {(user?.publicMetadata?.credits as number) ?? 0}
              </p>
            </div>
          </div>
        </div>

        {/* ── Billing / credit packs ───────────────────────────────── */}
        <div className="bg-bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-text-primary">
              Studio credits
            </h2>
            <p className="text-xs text-text-secondary leading-relaxed">
              Top up your credit balance. Purchases are handled by{' '}
              <a
                href="https://polar.sh"
                target="_blank"
                rel="noreferrer"
                className="text-neon hover:underline"
              >
                Polar.sh
              </a>{' '}
              as merchant of record — they handle VAT and local payment
              methods. Credits never expire.
            </p>
          </div>

          {billingSuccess && (
            <div className="rounded-lg border border-neon/40 bg-neon/5 px-4 py-3 text-xs text-neon">
              Payment complete — credits will appear within a few seconds.
              Refresh this page if you don't see them.
            </div>
          )}

          {products.length === 0 ? (
            <p className="text-xs text-text-muted">
              No products configured yet. The admin needs to set the
              POLAR_PRODUCT_* secrets on the worker.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {products.map((p) => (
                <div
                  key={p.id}
                  className={`relative rounded-xl p-4 flex flex-col gap-2 transition-colors ${
                    p.highlighted
                      ? 'border-2 border-neon bg-neon/5 neon-glow'
                      : 'border border-border bg-bg-secondary'
                  }`}
                >
                  {p.highlighted && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-neon text-bg-primary">
                      Most popular
                    </span>
                  )}
                  <div className="space-y-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
                      {p.name}
                    </p>
                    <p className="text-2xl font-semibold text-text-primary">
                      R{p.price_zar.toLocaleString('en-ZA')}
                    </p>
                    <p className="text-[11px] text-text-muted">
                      ${p.price_usd.toFixed(2)} USD
                    </p>
                    <p className="text-xs text-neon font-semibold pt-1">
                      {p.credits.toLocaleString()} credits
                    </p>
                  </div>
                  <p className="text-[11px] text-text-secondary leading-relaxed flex-1">
                    {p.description}
                  </p>
                  <button
                    onClick={() => handleBuy(p.id)}
                    disabled={!p.available || buyingId !== null}
                    className="mt-1 px-3 py-2 text-xs font-semibold rounded-lg bg-neon text-bg-primary hover:bg-neon-dim transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    title={
                      p.available
                        ? 'Checkout via Polar'
                        : 'Not yet configured in Polar dashboard'
                    }
                  >
                    {buyingId === p.id
                      ? 'Redirecting…'
                      : p.available
                      ? `Buy ${p.name}`
                      : 'Coming soon'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Purchase history — task #9 */}
          {purchases.length > 0 && (
            <div className="pt-2 space-y-2">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
                Order history
              </h3>
              <div className="rounded-lg border border-border bg-bg-secondary overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-border text-text-muted">
                      <th className="text-left font-medium px-3 py-2">Date</th>
                      <th className="text-left font-medium px-3 py-2">Pack</th>
                      <th className="text-right font-medium px-3 py-2">Credits</th>
                      <th className="text-right font-medium px-3 py-2">Amount</th>
                      <th className="text-right font-medium px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((p) => {
                      const date = new Date(p.created_at).toLocaleDateString()
                      const amount =
                        p.amount_cents !== null && p.currency
                          ? `${p.currency.toUpperCase()} ${(p.amount_cents / 100).toFixed(2)}`
                          : '—'
                      return (
                        <tr
                          key={p.id}
                          className="border-b border-border last:border-0 hover:bg-bg-elevated"
                        >
                          <td className="px-3 py-2 text-text-secondary">{date}</td>
                          <td className="px-3 py-2 text-text-primary font-mono">
                            {p.catalog_id ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-neon font-semibold">
                            +{p.credits_granted.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right text-text-secondary">
                            {amount}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span
                              className={`font-mono text-[10px] uppercase tracking-wider ${
                                p.status === 'complete'
                                  ? 'text-neon'
                                  : p.status === 'refunded'
                                  ? 'text-warning'
                                  : 'text-text-muted'
                              }`}
                            >
                              {p.status}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {billingMsg && (
            <p
              className={`text-[11px] ${
                billingMsg.kind === 'ok' ? 'text-neon' : 'text-error'
              }`}
            >
              {billingMsg.text}
            </p>
          )}
        </div>

        <div className="bg-bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-text-primary">
              Replicate API key
            </h2>
            <p className="text-xs text-text-secondary leading-relaxed">
              Add your own Replicate token to unlock FLUX Dev, FLUX 1.1 Pro, and
              custom LoRA training. Generations on these models will bill your
              Replicate account directly — your studio credits won't decrement.
              Without a personal key, the studio's shared key is used and your
              credits are charged.
            </p>
            <p className="text-[11px] text-text-muted">
              Your key is encrypted with AES-GCM before being stored. Get a token
              at{' '}
              <a
                href="https://replicate.com/account/api-tokens"
                target="_blank"
                rel="noreferrer"
                className="text-neon hover:underline"
              >
                replicate.com/account/api-tokens
              </a>
              .
            </p>
          </div>

          {keyState?.has_key ? (
            <div className="flex items-center justify-between rounded-lg border border-neon/30 bg-neon/5 px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-2 h-2 rounded-full bg-neon shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-text-primary">
                    Personal key active
                  </p>
                  <p className="text-[11px] text-text-muted font-mono truncate">
                    {keyState.preview ?? '••••'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDeleteKey}
                disabled={keyBusy}
                className="text-[11px] px-3 py-1.5 rounded-lg border border-border hover:border-error hover:text-error text-text-secondary transition-colors cursor-pointer disabled:opacity-50"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="r8_xxxxxxxxxxxxxxxxxxxx"
                  disabled={keyBusy}
                  className="input-glow flex-1 bg-bg-secondary border border-border rounded-lg px-3 py-2 text-sm font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:border-neon transition-all disabled:opacity-50"
                />
                <button
                  onClick={handleSaveKey}
                  disabled={keyBusy || !keyInput.trim()}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-neon text-bg-primary hover:bg-neon-dim transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {keyBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {keyMsg && (
            <p
              className={`text-[11px] ${
                keyMsg.kind === 'ok' ? 'text-neon' : 'text-error'
              }`}
            >
              {keyMsg.text}
            </p>
          )}
        </div>

        {/* Clerk UserProfile — we deliberately do NOT wrap this in our own
            bg-bg-card surface anymore. The dark baseTheme paints its own
            dark card; overlaying our card on top of it caused dark-on-dark
            text bleed in several internal sections. Outer wrapper is
            transparent so Clerk's preset owns the full visual treatment. */}
        <div className="rounded-xl overflow-hidden">
          <UserProfile
            appearance={{
              baseTheme: dark,
              elements: {
                rootBox: 'w-full',
                navbar: 'hidden',
                profileSection__activeDevices: 'hidden',
              },
              variables: {
                colorPrimary: '#39FF14',
                borderRadius: '0.75rem',
              },
            }}
          />
        </div>
      </div>
    </div>
  )
}
